import { Component } from '@theme/component';

/**
 * Accordion list where opening an item reveals its content and swaps the
 * image shown beside the list.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} media - The column that holds the image slides.
 * @property {HTMLDetailsElement[]} [items] - The accordion items.
 * @property {HTMLElement[]} [images] - The image slides, one per item, in item order.
 *
 * @extends {Component<Refs>}
 */
class AccordionSwapComponent extends Component {
  requiredRefs = ['media'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#placeSlides();

    const items = this.refs.items ?? [];
    const initial = items.find((item) => item.open) ?? items[0];

    if (!initial) return;

    initial.open = true;
    this.#activate(initial);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#placeSlides();

    const open = (this.refs.items ?? []).find((item) => item.open);

    if (open) this.#activate(open);
  }

  /**
   * Moves each item's slide into the media column, keeping item order.
   */
  #placeSlides() {
    const { media } = this.refs;

    for (const item of this.refs.items ?? []) {
      const slide = item.querySelector(':scope > .accordion-swap__content > .image-text-swap__image');

      if (slide) media.append(slide);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Keeps a single item open and shows its image.
   * @param {Event} event
   */
  onToggle(event) {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    for (const item of this.refs.items ?? []) {
      if (item !== details && item.open) item.open = false;
    }

    this.#activate(details);
  }

  /**
   * Opens the item a merchant selects in the theme editor.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const details = target.closest('details');

    if (details instanceof HTMLDetailsElement && this.contains(details)) {
      details.open = true;
    }
  };

  /**
   * Shows the image rendered at the same position as the open item.
   * @param {HTMLDetailsElement} details
   */
  #activate(details) {
    const index = (this.refs.items ?? []).indexOf(details);
    const slides = [...this.refs.media.querySelectorAll(':scope > .image-text-swap__image')];

    slides.forEach((slide, slideIndex) => {
      slide.toggleAttribute('data-active', slideIndex === index);
    });
  }
}

if (!customElements.get('accordion-swap-component')) {
  customElements.define('accordion-swap-component', AccordionSwapComponent);
}
